/**
 * Contacts Management Page
 * Cold leads/data for cold DM/email
 */
import React, { useState, useEffect } from 'react';
import { useRestorableState, useScrollMemory } from '../hooks/useListMemory';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchInput } from '../components/ui/SearchInput';
import { useApp } from '../App';
import { useAppSelector } from '../store/hooks';
import { selectHasPermission } from '../store/slices/authSlice';
import { PageLayout } from '../components/layout/PageLayout';
import { NavLink } from 'react-router-dom';
import { Users, UserCircle, UserPlus, Edit, Trash2, Building2, Mail, IdCard } from 'lucide-react';
import { cn } from '../lib/utils';
import { Tooltip } from '../UI/Tooltip';
import { DataTable } from '../components/ui/DataTable';
import { Pagination } from '../components/ui/Pagination';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { marketingAPI, Contact, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../lib/marketing-api';

export const ContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useApp();
  const canView = useAppSelector(selectHasPermission('marketing.view_contact'));
  const canCreate = useAppSelector(selectHasPermission('marketing.create_contact'));
  const canEdit = useAppSelector(selectHasPermission('marketing.edit_contact'));
  const canDelete = useAppSelector(selectHasPermission('marketing.delete_contact'));
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useRestorableState('contacts:page', 1);
  const [pageSize, setPageSize] = useRestorableState('contacts:pageSize', DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  useScrollMemory('contacts', !isLoading);
  const [searchTerm, setSearchTerm] = useRestorableState('contacts:searchTerm', '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useRestorableState('contacts:debouncedSearchTerm', '');
  const [deleteContactId, setDeleteContactId] = useState<number | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!canView) {
      showToast('You do not have permission to view contacts', 'error');
      return;
    }
    loadData();
  }, [canView, debouncedSearchTerm, page, pageSize]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await marketingAPI.getContacts({
        page,
        page_size: pageSize,
        search: debouncedSearchTerm || undefined
      });
      setContacts(res.items);
      setTotal(res.total);
      setTotalPages(res.total_pages);
    } catch (error: any) {
      showToast(error.message || 'Failed to load contacts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const openDeleteContactConfirm = (id: number) => {
    if (!canDelete) {
      showToast('You do not have permission to delete contacts', 'error');
      return;
    }
    setDeleteContactId(id);
  };

  const handleConfirmDeleteContact = async () => {
    if (deleteContactId == null) return;
    try {
      await marketingAPI.deleteContact(deleteContactId);
      showToast('Contact deleted successfully', 'success');
      loadData();
    } catch (error: any) {
      showToast(error.message || 'Failed to delete contact', 'error');
    }
  };

  // Contacts are already filtered by API
  const filteredContacts = contacts;

  const underDatabase = location.pathname.startsWith('/database');
  const breadcrumbsBase = underDatabase
    ? [{ label: 'Database', href: '/database' }, { label: 'Contacts', href: '/database/contacts' }]
    : [{ label: 'Contacts', href: '/contacts' }];

  if (!canView) {
    return (
      <PageLayout
        title="Contacts"
        description="Manage individual contacts. Link contacts to organizations and plants, and convert them to leads."
        breadcrumbs={breadcrumbsBase}
      >
        <Card>
          <div className="text-center py-12">
            <p className="text-slate-600">You do not have permission to view contacts.</p>
            <p className="text-sm text-slate-500 mt-2">Required permission: marketing.view_contact</p>
          </div>
        </Card>
      </PageLayout>
    );
  }

  const breadcrumbs = breadcrumbsBase;

  const actions = canCreate ? (
    <Button
      size="sm"
      onClick={() => navigate('/contacts/new')}
      leftIcon={<UserPlus size={14} strokeWidth={3} />}
    >
      Add Contact
    </Button>
  ) : null;

  return (
    <PageLayout
      title="Contacts"
      description="Manage individual contacts. Link contacts to organizations and plants, and convert them to leads."
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      {/* Consolidated Command Bar */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm px-5 py-0 h-14 mb-4 flex items-center">
        <div className="flex items-center justify-between gap-6 w-full h-full flex-wrap lg:flex-nowrap">
          {/* Internal Navigation group */}
          <div className="flex items-center h-full">
            <nav className="flex gap-6 h-full">
              {[
                { path: '/database/organizations', label: 'Organizations', icon: Building2, permission: 'marketing.view_organization' },
                { path: '/database/customers', label: 'Customers', icon: Users, permission: 'marketing.view_customer' },
                { path: '/database/contacts', label: 'Contacts', icon: UserCircle, permission: 'marketing.view_contact' },
                { path: '/database/visiting-card-contacts', label: 'Visiting Cards', icon: IdCard, permission: 'marketing.view_visiting_card_contact' },
              ].map((tab) => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 h-full px-1 text-sm font-bold transition-all border-b-2 -mb-[1px] relative z-10',
                      isActive
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    )
                  }
                >
                  <tab.icon size={14} strokeWidth={2.5} />
                  <span className="uppercase tracking-widest text-[11px] whitespace-nowrap">{tab.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Centered Vertical Divider */}
          <div className="hidden lg:block w-[1px] h-5 bg-slate-200" />

          {/* Action group: search */}
          <div className="flex flex-1 items-center gap-3">
            <SearchInput
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm('')}
              containerClassName="max-w-md shadow-none"
            />
          </div>
        </div>
      </div>

      <div className="mt-4">
        {/* Contacts List */}
        <Card noPadding contentClassName="py-0" className="overflow-hidden">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-slate-600">Loading contacts...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">No contacts found</p>
            </div>
          ) : (
            <>
            <DataTable<Contact>
              bordered={false}
              data={filteredContacts}
              rowKey={(c) => c.id}
              dense={true}
              showVerticalLines={true}
              columns={[
                {
                  key: 'organization',
                  label: 'Company',
                  render: (contact) => (
                    <div>
                      <div className="font-medium text-slate-900">{contact.organization?.name ?? '—'}</div>
                      {contact.organization?.industry && <div className="text-xs text-slate-500 mt-1">{contact.organization.industry}</div>}
                    </div>
                  ),
                },
                {
                  key: 'contact_person_name',
                  label: 'Contact Person',
                  render: (contact) =>
                    contact.contact_person_name ? (
                      <div>
                        <div className="text-sm text-slate-900">{contact.contact_person_name}</div>
                        {contact.contact_job_title && <div className="text-xs text-slate-500">{contact.contact_job_title}</div>}
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    ),
                },
                {
                  key: 'contact_email',
                  label: 'Email',
                  render: (contact) =>
                    contact.contact_email ? (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Mail size={14} />
                        {contact.contact_email}
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    ),
                },
                {
                  key: 'plant_location',
                  label: 'Plant / Location',
                  render: (contact) => {
                    if (!contact.plant) return <span className="text-slate-400 text-sm">-</span>;
                    const parts = [contact.plant.city, contact.plant.state, contact.plant.country].filter(Boolean);
                    return (
                      <div>
                        <div className="text-sm text-slate-900">{contact.plant.plant_name}</div>
                        {parts.length > 0 && <div className="text-xs text-slate-500">{parts.join(', ')}</div>}
                      </div>
                    );
                  },
                },
                {
                  key: 'actions',
                  label: '',
                  sortable: false,
                  align: 'right',
                  render: (contact) => (
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <Tooltip content="Edit Contact">
                          <Button
                            variant="ghost"
                            size="xs"
                            className="w-8 h-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/contacts/${contact.id}/edit`);
                            }}
                          >
                            <Edit size={16} />
                          </Button>
                        </Tooltip>
                      )}
                      {canDelete && !contact.is_converted && (
                        <Tooltip content="Delete Contact">
                          <Button
                            variant="ghost"
                            size="xs"
                            className="w-8 h-8 p-0 text-rose-500 hover:text-rose-600 hover:bg-transparent transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteContactConfirm(contact.id);
                            }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  ),
                },
              ]}
            />
            <div className="border-t border-slate-200 px-4 py-3">
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            </div>
            </>
          )}
        </Card>
      </div>

      <ConfirmModal
        isOpen={deleteContactId != null}
        onClose={() => setDeleteContactId(null)}
        onConfirm={handleConfirmDeleteContact}
        title="Delete contact"
        message="Are you sure you want to delete this contact?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
    </PageLayout>
  );
};
